<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Spinner } from '@/components/ui/spinner'
import { Bot, Plus, MessageSquare } from 'lucide-vue-next'

const props = defineProps({
  layerId: {
    type: Number,
    required: true
  },
  layerName: {
    type: String,
    default: 'Layer'
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const store = useStore()
const route = useRoute()
const router = useRouter()
const orgId = computed(() => route.params.org_id)

const isPopoverOpen = ref(false)
const isCreatingChat = ref(false)

const chatSessions = computed(() => {
  return store.getters['chat/getOrgSessions'](orgId.value)
})

watch(isPopoverOpen, (open) => {
  if (open) {
    store.dispatch('chat/fetchSessions', { orgId: orgId.value })
  }
})

const createNewChat = async () => {
  isPopoverOpen.value = false
  if (isCreatingChat.value) return
  isCreatingChat.value = true
  try {
    const session = await store.dispatch('chat/createSession', {
      orgId: orgId.value,
      name: `Ask AI: ${props.layerName}`
    })
    await store.dispatch('chat/linkLayerPromptAction', {
      orgId: orgId.value,
      sessionUuid: session.session_code,
      layerId: props.layerId
    })
    router.push({
      name: 'ChatSession',
      params: { org_id: orgId.value, session_code: session.session_code }
    })
  } catch (error) {
    console.error('Failed to start AI chat:', error)
    toast({
      title: 'Failed to start AI chat',
      description: 'Please try again',
      variant: 'destructive'
    })
  } finally {
    isCreatingChat.value = false
  }
}

const addToExistingChat = async (session) => {
  isPopoverOpen.value = false
  if (isCreatingChat.value) return
  isCreatingChat.value = true
  try {
    await store.dispatch('chat/linkLayerPromptAction', {
      orgId: orgId.value,
      sessionUuid: session.session_code,
      layerId: props.layerId
    })
    router.push({
      name: 'ChatSession',
      params: { org_id: orgId.value, session_code: session.session_code }
    })
  } catch (error) {
    console.error('Failed to add to chat:', error)
    toast({
      title: 'Failed to add to chat',
      description: 'Please try again',
      variant: 'destructive'
    })
  } finally {
    isCreatingChat.value = false
  }
}
</script>

<template>
  <Popover v-model:open="isPopoverOpen">
    <PopoverTrigger as-child>
      <Button
        :disabled="disabled || isCreatingChat"
        variant="outline"
        size="sm"
        class="border-primary text-primary hover:bg-primary hover:text-white"
      >
        <Spinner v-if="isCreatingChat" class="h-4 w-4 mr-2" />
        <Bot v-else class="h-4 w-4 mr-2" />
        {{ isCreatingChat ? 'Starting...' : 'Ask AI' }}
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-64 p-0" align="end">
      <div
        @click="createNewChat"
        class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent border-b"
      >
        <Plus class="h-4 w-4" />
        <span class="text-sm font-medium">New Chat</span>
      </div>
      <div class="max-h-48 overflow-y-auto">
        <div
          v-for="session in chatSessions"
          :key="session.session_code"
          @click="addToExistingChat(session)"
          class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent"
        >
          <MessageSquare class="h-4 w-4 shrink-0" />
          <span class="text-sm truncate">{{ session.name }}</span>
        </div>
        <div v-if="chatSessions.length === 0" class="px-3 py-2 text-sm text-muted-foreground">
          No existing chats
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
